export type Coordinate = [number, number];

/**
 * A longitude in the turn of the world nearest `ref`, within 180° of it: how a line across the
 * dateline names its points, running on past 180° (Tarawa at 172.93° to Kiritimati at -157.47°
 * is 172.93° to 202.53°), and how a point is compared with one such line.
 */
export function nearLng(lng: number, ref: number): number {
  return lng + 360 * Math.round((ref - lng) / 360);
}

/** Degrees to five decimals, about a metre: all a map or a page needs (the home map 18% smaller gzipped than at 16). */
export function toMetre(degrees: number): number {
  return Math.round(degrees * 1e5) / 1e5;
}

/** A place as a map point, `[lng, lat]`, to a metre. */
export function lngLat(place: { longitude: number; latitude: number }): Coordinate {
  return [toMetre(place.longitude), toMetre(place.latitude)];
}

/**
 * Calculate the great-circle distance between two points using the Haversine formula.
 * Returns the distance in kilometers; longitudes a turn of the world apart are the same place.
 */
export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}
